import { Path } from 'slate';
import { cloneDeep } from 'lodash';
import {
  pathWasRemoved,
  textOpBounds,
  merge,
  updateOrRemove,
  adjustNodePathForNodeMove,
  adjustPositionForNodeMove,
  adjustPathForNodeSplit,
  adjustPositionForNodeSplit,
  adjustPathForNodeMerge,
  adjustPositionForNodeMerge,
  adjustPathForNodeInsert,
  adjustPathForNodeRemove,
  changeRange,
} from './utils';

const transformCursor = (cursor, cursorDetails, ops, _isOwnOp) => {
  // isOwnOp should return the final editing position of op, instead
  // of transforming cursor -- but I don't need that.

  return ops.reduce((cursor, op) => {
    switch (op.type) {
      case 'insert_text': {
        if (cursorDetails.removed) return cursor;
        if (!Path.equals(cursor.path, op.path)) return cursor;
        if (op.offset > cursor.offset) return cursor;
        return merge(cursor, { offset: cursor.offset + op.text.length });
      }
      case 'remove_text': {
        if (cursorDetails.removed) return cursor;
        if (!Path.equals(cursor.path, op.path)) return cursor;
        const deleteBounds = textOpBounds(op);
        if (deleteBounds.start >= cursor.offset) return cursor;
        if (deleteBounds.end <= cursor.offset) {
          return merge(cursor, { offset: cursor.offset - op.text.length });
        }
        return merge(cursor, { offset: deleteBounds.start });
      }
      case 'split_node': {
        if (cursorDetails.removed) {
          return merge(cursor, {
            path: adjustPathForNodeSplit(cursor.path, -1, op, false),
          });
        } else {
          return merge(cursor, {
            path: adjustPathForNodeSplit(cursor.path, cursor.offset, op, true),
            offset: adjustPositionForNodeSplit(
              cursor.path,
              cursor.offset,
              op,
              true
            ),
          });
        }
      }
      case 'merge_node': {
        if (cursorDetails.removed) {
          return merge(cursor, {
            path: adjustPathForNodeMerge(cursor.path, op, false),
          });
        } else {
          return merge(cursor, {
            path: adjustPathForNodeMerge(cursor.path, op),
            offset: adjustPositionForNodeMerge(cursor.path, cursor.offset, op),
          });
        }
      }
      case 'insert_node': {
        return merge(cursor, {
          path: adjustPathForNodeInsert(cursor.path, op, true),
        });
      }
      case 'remove_node': {
        if (pathWasRemoved(cursor.path, op.path)) {
          // Temporarily put the cursor in the node that was removed,
          // so we can keep track of where it was and transform that
          // position. When we fix things up with an actual value, we
          // can compare paths to put it in the nearest text node.
          const tCursor = merge(cursor, {
            path: op.path,
            offset: 0,
          });
          cursorDetails.removed = true;

          return tCursor;
        }

        return merge(cursor, {
          path: adjustPathForNodeRemove(cursor.path, op, false),
        });
      }
      case 'move_node': {
        if (cursorDetails.removed) {
          return merge(cursor, {
            path: adjustPositionForNodeMove(cursor.path, op),
          });
        } else {
          return merge(cursor, {
            path: adjustNodePathForNodeMove(cursor.path, op),
          });
        }
      }
      case 'set_node':
      case 'add_mark':
      case 'remove_mark':
      case 'set_mark':
      case 'add_annotation':
      case 'remove_annotation':
      case 'set_annotation':
      case 'set_selection':
      case 'set_value':
      case 'set_metadata':
      case 'add_metadata':
      case 'remove_metadata': {
        return cursor;
      }
    }
    console.warn("couldn't find a cursor transform for " + op.type);
    return cursor;
  }, cursor);
};

/**
 * Transforms `range`'s anchor point and focus point against a set
 * of ops. `data` contains information about whether an endpoint is
 * added or removed, and is modified in-place. For example, if `ops`
 * deletes a node containing the anchor point, this data object will
 * include `{ anchorRemoved: true }`. This allows us to fix up the
 * cursor position once we're given a value and can find a new text
 * node to move it to. (Operations don't carry around the document, so
 * we don't know which paths refer to actual nodes).
 */
function transformRange(range, data, ops) {
  return changeRange(range, data, (cursor, cursorDetails) =>
    transformCursor(cursor, cursorDetails, ops, false)
  );
}

/**
 * Transforms the range referred to by `rangeOp[key]` against `op`,
 * returning a new operation with the transformed selection. If an
 * endpoint of the range was removed, `op.data[key]` will indicate it
 * with a flag: { [key]: { anchorRemoved: true } }
 */
function transformOpRange(rangeOp, key, op) {
  let extraData = {};
  if (rangeOp.data && rangeOp.data[key]) {
    extraData = rangeOp.data[key];
  }

  const tOp = cloneDeep(rangeOp);
  tOp[key] = transformRange(rangeOp[key], extraData, [op]);

  return updateOrRemove(tOp, ['data', key], extraData);
}

export { transformCursor, transformOpRange };
